import { cn, withErrorBoundary } from '@navi-app/utils';
import { Spinner } from 'baseui/spinner';
import { LabelLarge, LabelMedium, LabelSmall } from 'baseui/typography';
import ReactDOM from 'react-dom';

type TProps = {
  isLoading: boolean;
  customClass?: string;
  isFullPage?: boolean;
  spinnerSize?: 'small' | 'medium' | 'large';
  label?: string;
  labelPlacement?: 'top' | 'right' | 'left' | 'bottom';
  labelSize?: 'small' | 'medium' | 'large';
  customSizeSpinner?: string;
};

const LoadingState = ({
  isLoading,
  customClass,
  isFullPage = false,
  spinnerSize = 'medium',
  label = 'Loading...',
  labelPlacement = 'bottom',
  labelSize = 'medium',
  customSizeSpinner,
}: TProps) => {
  if (!isLoading) {
    return null;
  }

  const LoadingIndicator = () => {
    let size = 'w-12 h-12';
    let LabelComponent = <LabelMedium>{label}</LabelMedium>;

    if (spinnerSize === 'small') {
      size = '!w-8 !h-8 !border-[5px]';
    } else if (spinnerSize === 'medium') {
      size = '!w-10 !h-10 !border-[6px]';
    } else if (spinnerSize === 'large') {
      size = '!w-14 !h-14 !border-[7px]';
    }

    if (customSizeSpinner) {
      size = customSizeSpinner;
    }

    if (labelSize === 'small') {
      LabelComponent = <LabelSmall>{label}</LabelSmall>;
    } else if (labelSize === 'medium') {
      LabelComponent = <LabelMedium>{label}</LabelMedium>;
    } else if (labelSize === 'large') {
      LabelComponent = <LabelLarge>{label}</LabelLarge>;
    }

    return (
      <div
        className={cn(
          'flex items-center justify-center gap-3 h-full',
          labelPlacement === 'top' && 'flex-col-reverse',
          labelPlacement === 'left' && 'flex-row-reverse',
          labelPlacement === 'right' && 'flex-row',
          labelPlacement === 'bottom' && 'flex-col',
          customClass
        )}
      >
        <Spinner className={size} />
        {label && LabelComponent}
      </div>
    );
  };

  if (isFullPage) {
    return ReactDOM.createPortal(
      <div className="fixed h-screen w-full top-0 left-0 bg-opacity-50 bg-slate-500 z-[999]">
        <div className="flex justify-center top-1/2 h-screen items-center">
          <LoadingIndicator />
        </div>
      </div>,
      document.body
    );
  }

  return <LoadingIndicator />;
};

export default withErrorBoundary(LoadingState);
